import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dahboradpage.css';
import moment from 'moment';
import Cookies from 'js-cookie';
import axios from 'axios';
import { Button } from 'antd';
import {
  CalendarOutlined,
  CheckSquareOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
  SyncOutlined,
  MailOutlined,
  TruckOutlined,
  ArrowRightOutlined,
  RightOutlined,
} from '@ant-design/icons';
import DashboardEmailModal from './DashboardEmailModal';
import { FaUser, FaHourglassHalf } from 'react-icons/fa';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import config from '../config';

function DashboardPage({ darkTheme = false, packingLiveUpdate = null, livePackingUpdatesEnabled = false }) {
  const [todayStats, setTodayStats] = useState({ todayOrdersCount: 0, todayCompletedCount: 0 });
  const [overallStats, setOverallStats] = useState({ totalOrdersCount: 0, overallCompletedCount: 0 });
  const [ordersData, setOrdersData] = useState({ labels: [], orders: [], units: [] });
  const [scheduledForPackingToday, setScheduledForPackingToday] = useState(0);
  const [todayNotCompletedCount, setTodayNotCompletedCount] = useState(0);
  const [courierShipments, setCourierShipments] = useState([]);
  const [packedPersons, setPackedPersons] = useState([]);
  const [pickedPersons, setPickedPersons] = useState([]);
  const [warehouseOutData, setWarehouseOutData] = useState({ yes: 0, no: 0, unknown: 0 });
  const [stockIssues, setStockIssues] = useState([]);
  const [todayPickedCount, setTodayPickedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [trendSeries, setTrendSeries] = useState('both'); // 'both' | 'orders' | 'units'
  const [livePackingUpdates, setLivePackingUpdates] = useState([]);
  const [packingMaterialUsage, setPackingMaterialUsage] = useState([]);
  const [packingPerformance, setPackingPerformance] = useState({
    packedPerHour: [],
    productivityTrend: [],
    averagePackingTimeMs: 0,
    completedSessions: 0,
    byEmployee: [],
  });
  const [correctionStats, setCorrectionStats] = useState([]);
  const [activeLoginUsers, setActiveLoginUsers] = useState([]);

  useEffect(() => {
    if (!packingLiveUpdate || !livePackingUpdatesEnabled) return;
    setLivePackingUpdates((previous) => [
      {
        orderID: packingLiveUpdate.orderID,
        packedBy: packingLiveUpdate.packedBy || 'Operator',
        packedAt: packingLiveUpdate.packedAt || packingLiveUpdate.receivedAt,
        status: packingLiveUpdate.status || 'Completed',
      },
      ...previous.filter((item) => item.orderID !== packingLiveUpdate.orderID),
    ].slice(0, 8));
  }, [packingLiveUpdate, livePackingUpdatesEnabled]);

  const defaultEmailRecipients = [
    'leelaprasad.paila@a2znaturopathy.com',
    'surya.behara@a2znaturopathy.com',
    'Anil.Kuchipudi@a2znaturopathy.com',
    'havilah.sirivella@a2znaturopathy.com',
    'keerthi.puppala@a2znaturopathy.com',
    'satish.gorantla@a2znaturopathy.com'
  ];

  const navigate = useNavigate();

  const getCourierAsset = (courier) => {
    const value = String(courier || '').toLowerCase();
    if (value.includes('amazon')) return { label: 'Amazon Delivery', image: '/Amazon.ico' };
    if (value.includes('delhivery') || value.includes('delivery surface')) return { label: 'Delhivery Surface', image: '/Delivery%20surface.png' };
    if (value.includes('dtdc')) return { label: 'DTDC Shipping', image: '/DTDC%20logo.webp' };
    return { label: courier || 'Internal Transfer', image: null };
  };

  const fetchDashboardData = async () => {
    setIsRefreshing(true);
    try {
      const token = Cookies.get('token');
      const userName = Cookies.get('userName');

      if (!token || !userName) {
        navigate('/');
        return;
      }

      const dayStart = moment().startOf('day').toISOString();
      const dayEnd = moment().add(1, 'day').startOf('day').toISOString();
      const [statsResponse, chartResponse, materialMovementResponse] = await Promise.all([
        axios.get(`${config.apiBaseUrl}/api/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${config.apiBaseUrl}/api/chart-data`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${config.apiBaseUrl}/api/packing-material-reports/daily`, {
          params: { startDate: dayStart, endDate: dayEnd },
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { summary: [] } }))
      ]);

      const statsData = statsResponse.data;
      const chartData = chartResponse.data;
      const usageByMaterial = new Map();
      (materialMovementResponse.data?.summary || []).forEach((movement) => {
        if (String(movement.type || '').toUpperCase() !== 'DEDUCTION') return;
        const material = movement.material || 'Unknown material';
        usageByMaterial.set(material, (usageByMaterial.get(material) || 0) + Number(movement.quantity || 0));
      });
      setPackingMaterialUsage(
        [...usageByMaterial.entries()]
          .map(([material, quantity]) => ({ material, quantity }))
          .sort((a, b) => b.quantity - a.quantity)
      );

      setTodayStats(statsData.todayStats || { todayOrdersCount: 0, todayCompletedCount: 0 });
      setOverallStats(statsData.overallStats || { totalOrdersCount: 0, overallCompletedCount: 0 });
      setWarehouseOutData(statsData.warehouseOutData || { yes: 0, no: 0, unknown: 0 });
      setCourierShipments(Array.isArray(statsData.courierShipments) ? statsData.courierShipments : []);
      setPackedPersons(
        Array.isArray(statsData.packedPersons)
          ? statsData.packedPersons.map((p) => ({ name: p._id, count: p.count }))
          : []
      );
      setTodayPickedCount(statsData.todayPickedCount || 0);
      setPickedPersons(Array.isArray(statsData.todayPickedUserWise) ? statsData.todayPickedUserWise : []);
      setPackingPerformance(statsData.packingPerformance || {
        packedPerHour: [], productivityTrend: [], averagePackingTimeMs: 0, completedSessions: 0, byEmployee: []
      });
      setCorrectionStats(Array.isArray(statsData.correctionStats) ? statsData.correctionStats : []);
      setActiveLoginUsers(Array.isArray(statsData.activeLoginUsers) ? statsData.activeLoginUsers : []);
      setScheduledForPackingToday(statsData.scheduledForPackingToday || 0);
      setTodayNotCompletedCount(statsData.todayNotCompletedCount || 0);

      // Stock issues – normalise possible shapes
      const rawIssues = statsData.stockIssues || statsData.stock_issues || [];
      setStockIssues(
        Array.isArray(rawIssues)
          ? rawIssues.map((i) => ({
              _id: i._id || i.name || i.issue || 'Unknown',
              count: Number(i.count) || 0,
            }))
          : []
      );

      if (chartData && chartData.labels) {
        const formattedOrdersData = chartData.labels.map((label, index) => ({
          label,
          orders: chartData.orders[index],
          units: chartData.units[index],
        }));
        setOrdersData(formattedOrdersData);
      }

      setLastUpdateTime(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchDashboardData();
  };

  const handleStockIssueClick = (issueName) => {
    navigate('/orders', {
      state: {
        filterByStockIssue: issueName,
        dateRange: [moment().startOf('day').toISOString(), moment().endOf('day').toISOString()],
      },
    });
  };

  /* ───── Display helpers ───── */
  const displayName = (Cookies.get('userName') || '').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const pct = (n, of) => (of ? Math.min(100, Math.round((Number(n || 0) / of) * 100)) : 0);
  const completedCount = Number(todayStats.todayCompletedCount) || 0;
  const completionPct = pct(completedCount, scheduledForPackingToday);

  const stages = [
    {
      key: 'scheduled',
      label: 'Scheduled',
      value: scheduledForPackingToday,
      icon: <CalendarOutlined />,
      tone: 'blue',
      caption: 'Due for packing today',
    },
    {
      key: 'picked',
      label: 'Picked',
      value: todayPickedCount,
      icon: <CheckSquareOutlined />,
      tone: 'amber',
      caption: `${pct(todayPickedCount, scheduledForPackingToday)}% of scheduled`,
      bar: pct(todayPickedCount, scheduledForPackingToday),
    },
    {
      key: 'completed',
      label: 'Completed',
      value: todayStats.todayCompletedCount,
      icon: <CheckCircleOutlined />,
      tone: 'green',
      caption: `${completionPct}% of scheduled`,
      bar: completionPct,
    },
    {
      key: 'pending',
      label: 'Pending',
      value: todayNotCompletedCount,
      icon: <ExclamationCircleOutlined />,
      tone: 'red',
      caption: 'Still to be packed',
      bar: pct(todayNotCompletedCount, scheduledForPackingToday),
    },
  ];

  const trendData = Array.isArray(ordersData) ? ordersData : [];

  const courierRows = [...(Array.isArray(courierShipments) ? courierShipments : [])].sort(
    (a, b) => (Number(b.count) || 0) - (Number(a.count) || 0)
  );
  const courierTotal = courierRows.reduce((s, c) => s + (Number(c.count) || 0), 0);

  // ── Picking side ──
  const pickingRows = [...pickedPersons]
    .map((p) => ({
      name: p.username || p.name || 'Unknown',
      count: Number(p.pickedCount ?? p.count) || 0,
      pending: false,
    }))
    .sort((a, b) => b.count - a.count);
  const pickingMax = Math.max(1, ...pickingRows.map((r) => r.count));
  const pickingTotal = pickingRows.reduce((s, r) => s + r.count, 0);
  const pickingAccuracy = pct(pickingTotal, scheduledForPackingToday);

  // ── Packing side ──
  const packingRows = [...packedPersons]
    .map((p) => ({
      name: p.name,
      count: Number(p.count) || 0,
      pending: p.name === 'Pending Orders' || p.name === 'Unknown Packer' || p.name === 'Unknown',
    }))
    .sort((a, b) => b.count - a.count);
  const packingMax = Math.max(1, ...packingRows.map((r) => r.count));
  const packingCompletedTotal = packingRows.filter((row) => !row.pending).reduce((s, r) => s + r.count, 0);
  const packingPendingTotal = packingRows.filter((row) => row.pending).reduce((s, r) => s + r.count, 0);
  const packingAccuracy = pct(completedCount, scheduledForPackingToday);
  const pickingStarPerformer = pickingRows[0] || null;
  const packingStarPerformer = packingRows.find((row) => !row.pending) || null;
  const packingMaterialTotal = packingMaterialUsage.reduce((sum, item) => sum + item.quantity, 0);

  const formatDuration = (milliseconds) => {
    const duration = Number(milliseconds) || 0;
    const minutes = Math.round(duration / 60000);
    if (duration > 0 && !minutes) return '<1m';
    if (!minutes) return '—';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };
  const hourlyPacking = Array.isArray(packingPerformance.packedPerHour) ? packingPerformance.packedPerHour : [];
  const packedCountByHour = new Map(hourlyPacking.map((item) => [Number(item.hour), Number(item.packedCount) || 0]));
  const hourlyWindowRows = Array.from({ length: 10 }, (_, index) => {
    const startHour = 10 + index;
    return {
      startHour,
      endHour: startHour + 1,
      packedCount: packedCountByHour.get(startHour) || 0,
    };
  });
  const hourlyWindowMax = Math.max(1, ...hourlyWindowRows.map((item) => item.packedCount));
  const peakPackingHour = hourlyPacking.reduce((best, item) => (
    Number(item.packedCount) > Number(best?.packedCount || 0) ? item : best
  ), null);
  const fastestPackingHour = hourlyPacking
    .filter((item) => Number(item.averageDurationMs) > 0)
    .reduce((best, item) => (!best || item.averageDurationMs < best.averageDurationMs ? item : best), null);
  const slowestPackingHour = hourlyPacking
    .filter((item) => Number(item.averageDurationMs) > 0)
    .reduce((best, item) => (!best || item.averageDurationMs > best.averageDurationMs ? item : best), null);
  const trendMax = Math.max(1, ...((packingPerformance.productivityTrend || []).map((item) => Number(item.packedCount) || 0)));
  const teamAnalytics = new Map();
  pickedPersons.forEach((person) => {
    const name = person.username || person.name || 'Unknown';
    teamAnalytics.set(name, {
      name,
      active: true,
      // Keep this metric identical to the Team activity Picking card:
      // one completed PickingActivity equals one picked operation.
      pickedOrders: Number(person.pickedCount) || 0,
      pickedQuantity: Number(person.pickedQuantity) || 0,
      packedOrders: 0,
      averageProcessingTimeMs: Number(person.averageProcessingTimeMs) || 0,
      corrections: 0,
    });
  });
  packedPersons.forEach((person) => {
    const name = person.name || 'Unknown Packer';
    if (name === 'Pending Orders') return;
    const current = teamAnalytics.get(name) || { name, active: true, pickedOrders: 0, pickedQuantity: 0, packedOrders: 0, averageProcessingTimeMs: 0, corrections: 0 };
    current.packedOrders = Number(person.count) || 0;
    teamAnalytics.set(name, current);
  });
  correctionStats.forEach((item) => {
    const name = item._id || 'Unknown';
    const current = teamAnalytics.get(name) || { name, active: true, pickedOrders: 0, pickedQuantity: 0, packedOrders: 0, averageProcessingTimeMs: 0, corrections: 0 };
    current.corrections = Number(item.correctionCount) || 0;
    teamAnalytics.set(name, current);
  });
  (packingPerformance.byEmployee || []).forEach((item) => {
    const current = teamAnalytics.get(item.name) || { name: item.name, active: true, pickedOrders: 0, pickedQuantity: 0, packedOrders: 0, averageProcessingTimeMs: 0, corrections: 0 };
    if (!current.averageProcessingTimeMs) current.averageProcessingTimeMs = Number(item.averageDurationMs) || 0;
    teamAnalytics.set(item.name, current);
  });
  const teamAnalyticsRows = [...teamAnalytics.values()].sort((a, b) => (b.pickedOrders + b.packedOrders) - (a.pickedOrders + a.packedOrders));

  // Warehouse
  const whYes = Number(warehouseOutData.yes) || 0;
  const whNo = Number(warehouseOutData.unknown ?? warehouseOutData.no) || 0;
  const whTotal = whYes + whNo;

  const stockIssueTotal = stockIssues.reduce((s, i) => s + (Number(i.count) || 0), 0);

  const axisColor = darkTheme ? '#82918A' : '#7A8781';
  const gridColor = darkTheme ? '#26352F' : '#E8EFEB';
  const tooltipStyle = {
    backgroundColor: darkTheme ? '#16211D' : '#fff',
    border: `1px solid ${darkTheme ? '#2A3933' : '#E2E9E5'}`,
    color: darkTheme ? '#F5F7F6' : '#17211D',
    borderRadius: '10px',
    padding: '10px 12px',
    boxShadow: darkTheme ? '0 8px 20px rgba(0, 0, 0, 0.3)' : '0 8px 24px rgba(15, 40, 28, 0.12)',
  };

  const renderSkeleton = () => (
    <div className="dv2-skeleton">
      <div className="dv2-skel dv2-skel-flow" />
      <div className="dv2-layout">
        <div className="dv2-main">
          <div className="dv2-skel dv2-skel-card" />
          <div className="dv2-skel dv2-skel-card short" />
        </div>
        <div className="dv2-rail">
          <div className="dv2-skel dv2-skel-card short" />
          <div className="dv2-skel dv2-skel-card short" />
          <div className="dv2-skel dv2-skel-card short" />
        </div>
      </div>
    </div>
  );

  // Helper to render one team list
  const renderTeamList = (rows, max, emptyMsg) =>
    rows.length > 0 ? (
      <ol className="dv2-team">
        {rows.map((row, index) => (
          <li key={`${row.name}-${index}`} className={index === 0 && !row.pending ? 'top' : ''}>
            <span className={`dv2-rank r${index + 1}`}>{index + 1}</span>
            <span className={`dv2-avatar${row.pending ? ' pending' : ''}`}>
              {row.pending ? (
                <FaHourglassHalf size={13} color="#FFFFFF" />
              ) : (
                <FaUser size={13} color="#FFFFFF" />
              )}
            </span>
            <div className="dv2-team-body">
              <div className="dv2-team-name">{row.name}</div>
              <div className="dv2-meter">
                <div style={{ width: `${Math.round((row.count / max) * 100)}%` }} />
              </div>
            </div>
            <b className="dv2-team-count">{row.count}</b>
          </li>
        ))}
      </ol>
    ) : (
      <div className="dv2-empty">{emptyMsg}</div>
    );

  return (
    <div className={`dv2 ${darkTheme ? 'dark' : 'light'}`}>
      <div className="dv2-shell">
        {/* ───── Top bar ───── */}
        <header className="dv2-top">
          <div className="dv2-top-left">
            <span className="dv2-eyebrow">{moment().format('dddd, D MMMM YYYY')}</span>
            <h1>
              {greeting}
              {displayName ? `, ${displayName}` : ''}
            </h1>
            <p>
              {loading
                ? "Loading today's activity…"
                : scheduledForPackingToday > 0
                ? `${completedCount} of ${scheduledForPackingToday} scheduled orders packed · ${completionPct}% done`
                : 'No orders are scheduled for packing yet today.'}
            </p>
          </div>
          <div className="dv2-top-right">
            <span className="dv2-live">
              <i />
              {lastUpdateTime ? `Updated ${lastUpdateTime.toLocaleTimeString()}` : 'Updating…'}
            </span>
            <div className="dv2-actions">
              <Button
                className="dv2-btn ghost"
                icon={<SyncOutlined spin={isRefreshing} />}
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                Refresh
              </Button>
              <Button className="dv2-btn ghost" onClick={() => navigate('/orders')}>
                Orders <ArrowRightOutlined />
              </Button>
              <Button
                type="primary"
                className="dv2-btn solid"
                icon={<MailOutlined />}
                onClick={() => setEmailModalVisible(true)}
              >
                Send report
              </Button>
            </div>
          </div>
        </header>

        {livePackingUpdatesEnabled && livePackingUpdates.length > 0 && (
          <section className="dv2-live-packing-card" aria-live="polite">
            <div className="dv2-live-packing-head">
              <div>
                <h2>Live packing updates</h2>
                <span>Newly completed packing activity</span>
              </div>
              <span className="dv2-live-badge"><i /> Live</span>
            </div>
            <div className="dv2-live-packing-list">
              {livePackingUpdates.map((item) => (
                <div className="dv2-live-packing-item" key={`${item.orderID}-${item.packedAt}`}>
                  <CheckCircleOutlined />
                  <div>
                    <strong>{item.packedBy} packed completed</strong>
                    <span>Order {item.orderID}</span>
                  </div>
                  <time>{moment(item.packedAt).format('DD MMM, hh:mm A')}</time>
                </div>
              ))}
            </div>
          </section>
        )}

        {loading ? (
          renderSkeleton()
        ) : (
          <>
            {/* ───── Order flow ───── */}
            <section className="dv2-flow" aria-label="Today's order flow">
              <div className="dv2-flow-stages">
                {stages.map((s) => (
                  <div key={s.key} className={`dv2-stage tone-${s.tone}`}>
                    <div className="dv2-stage-head">
                      <span className="dv2-stage-icon">{s.icon}</span>
                      <span className="dv2-stage-label">{s.label}</span>
                    </div>
                    <div className="dv2-stage-value">{s.value}</div>
                    <div className="dv2-stage-caption">{s.caption}</div>
                    {s.bar !== undefined && (
                      <div className="dv2-meter" aria-hidden="true">
                        <div style={{ width: `${s.bar}%` }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="dv2-flow-total">
                <span className="dv2-stage-icon">
                  <BarChartOutlined />
                </span>
                <div>
                  <div className="dv2-flow-total-value">{overallStats.totalOrdersCount}</div>
                  <div className="dv2-flow-total-label">Orders in the last 30 days</div>
                </div>
              </div>
            </section>

            <div className="dv2-layout">
              {/* ───── Main column ───── */}
              <main className="dv2-main">
                {/* Trend */}
                <section className="dv2-card">
                  <div className="dv2-card-head">
                    <div>
                      <h2>Orders & units</h2>
                      <span>Daily volume · last 30 days</span>
                    </div>
                    <div className="dv2-seg" role="tablist" aria-label="Series">
                      {[
                        ['both', 'Both'],
                        ['orders', 'Orders'],
                        ['units', 'Units'],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          role="tab"
                          aria-selected={trendSeries === key}
                          className={trendSeries === key ? 'on' : ''}
                          onClick={() => setTrendSeries(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="dv2-chart">
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={trendData} margin={{ top: 12, right: 16, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="dv2Orders" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={darkTheme ? '#3CCF87' : '#0F8A52'} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={darkTheme ? '#3CCF87' : '#0F8A52'} stopOpacity={0.02} />
                          </linearGradient>
                          <linearGradient id="dv2Units" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={darkTheme ? '#5BA3FF' : '#3B82F6'} stopOpacity={0.28} />
                            <stop offset="100%" stopColor={darkTheme ? '#5BA3FF' : '#3B82F6'} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
                        <XAxis dataKey="label" stroke={axisColor} fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke={axisColor} fontSize={12} tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={tooltipStyle} />
                        {trendSeries !== 'units' && (
                          <Area
                            type="monotone"
                            dataKey="orders"
                            name="Orders"
                            stroke={darkTheme ? '#3CCF87' : '#0F8A52'}
                            strokeWidth={2.5}
                            fill="url(#dv2Orders)"
                            dot={false}
                            activeDot={{ r: 5 }}
                          />
                        )}
                        {trendSeries !== 'orders' && (
                          <Area
                            type="monotone"
                            dataKey="units"
                            name="Units"
                            stroke={darkTheme ? '#5BA3FF' : '#3B82F6'}
                            strokeWidth={2.5}
                            fill="url(#dv2Units)"
                            dot={false}
                            activeDot={{ r: 5 }}
                          />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="dv2-chart-legend">
                    <span>
                      <i className="g" /> Orders
                    </span>
                    <span>
                      <i className="b" /> Units
                    </span>
                  </div>
                </section>

                {/* ───── Team activity – Picking | Packing side-by-side ───── */}
                <section className="dv2-card">
                  <div className="dv2-card-head">
                    <div>
                      <h2>Team activity</h2>
                      <span>Today’s quantity · agent performance</span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 0,
                      borderTop: '1px solid var(--line)',
                    }}
                  >
                    {/* Picking column */}
                    <div style={{ borderRight: '1px solid var(--line)', padding: '12px 8px 8px' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0 12px 10px',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>Picking</div>
                          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                            {pickingTotal} picked · {pickingAccuracy}% of scheduled
                          </div>
                        </div>
                        <span className="dv2-badge">{pickingAccuracy}%</span>
                      </div>
                      {renderTeamList(pickingRows, pickingMax, 'No picking data for today')}
                    </div>

                    {/* Packing column */}
                    <div style={{ padding: '12px 8px 8px' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0 12px 10px',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800 }}>Packing</div>
                          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                            {packingCompletedTotal} packed · {packingPendingTotal} pending · {packingAccuracy}% of scheduled
                          </div>
                        </div>
                        <span className="dv2-badge">{packingAccuracy}%</span>
                      </div>
                      {renderTeamList(packingRows, packingMax, 'No packing data for today')}
                    </div>
                  </div>
                </section>

                <div className="dv2-analytics-layout">
                {/* Packing material usage */}
                <section className="dv2-card dv2-material-usage dv2-analytics-material">
                  <div className="dv2-card-head">
                    <div>
                      <h2>Packing materials used</h2>
                      <span>Today’s deducted quantity by material</span>
                    </div>
                    <span className="dv2-badge">{packingMaterialTotal}</span>
                  </div>
                  {packingMaterialUsage.length > 0 ? (
                    <div className="dv2-material-list">
                      {packingMaterialUsage.map((item) => {
                        const share = packingMaterialTotal ? Math.round((item.quantity / packingMaterialTotal) * 100) : 0;
                        return (
                          <div className="dv2-material-row" key={item.material}>
                            <div className="dv2-material-row-head">
                              <span title={item.material}>{item.material}</span>
                              <b>{item.quantity}</b>
                            </div>
                            <div className="dv2-meter"><div style={{ width: `${share}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="dv2-empty">No packing materials moved today</div>
                  )}
                </section>

                {/* Additive analytics: uses the existing dashboard response only. */}
                <section className="dv2-card dv2-analytics-card dv2-analytics-performance">
                  <div className="dv2-card-head">
                    <div>
                      <h2>Packing performance</h2>
                      <span>Today’s throughput and processing speed</span>
                    </div>
                    <span className="dv2-badge">{todayStats.todayCompletedCount || 0} completed</span>
                  </div>
                  <div className="dv2-analytics-kpis">
                    <div><b>{formatDuration(packingPerformance.averagePackingTimeMs)}</b><span>Average packing time</span></div>
                    <div><b>{peakPackingHour ? `${String(peakPackingHour.hour).padStart(2, '0')}:00` : '—'}</b><span>Peak packing hour</span></div>
                    <div><b>{fastestPackingHour ? `${String(fastestPackingHour.hour).padStart(2, '0')}:00` : '—'}</b><span>Fastest average hour</span></div>
                    <div><b>{slowestPackingHour ? `${String(slowestPackingHour.hour).padStart(2, '0')}:00` : '—'}</b><span>Slowest average hour</span></div>
                    <div><b>{todayNotCompletedCount || 0}</b><span>Pending packing</span></div>
                  </div>
                  <div className="dv2-hourly-breakdown" aria-label="Orders packed by hour from 10 AM to 8 PM">
                    <div className="dv2-hourly-breakdown-title">Orders packed by hour <span>10:00 AM – 8:00 PM</span></div>
                    {hourlyWindowRows.map((item) => (
                      <div className="dv2-hourly-row" key={item.startHour}>
                        <span className="dv2-hourly-label">{String(item.startHour).padStart(2, '0')}:00 – {String(item.endHour).padStart(2, '0')}:00</span>
                        <div className="dv2-hourly-track"><div style={{ width: `${(item.packedCount / hourlyWindowMax) * 100}%` }} /></div>
                        <b>{item.packedCount}</b>
                      </div>
                    ))}
                  </div>
                  <div className="dv2-trend-heading">
                    <span>7-day packing productivity</span>
                    <small>Completed orders per day</small>
                  </div>
                  <div className="dv2-trend-strip">
                    {(packingPerformance.productivityTrend || []).map((item) => (
                      <div key={item.date} title={`${item.packedCount} packed`}>
                        <span style={{ height: `${Math.max(8, (Number(item.packedCount) / trendMax) * 100)}%` }} />
                        <small>{item.date.slice(5)}</small>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="dv2-card dv2-analytics-card dv2-analytics-users">
                  <div className="dv2-card-head">
                    <div>
                      <h2>User &amp; team analytics</h2>
                      <span>Active operational users and workload</span>
                    </div>
                      <span className="dv2-badge">{activeLoginUsers.length} active now</span>
                  </div>
                  {teamAnalyticsRows.length ? (
                    <div className="dv2-user-table-wrap">
                      <table className="dv2-user-table">
                        <thead><tr><th>User</th><th>Picked</th><th>Packed</th><th>Handled</th><th>Avg time</th><th>Corrections</th></tr></thead>
                        <tbody>{teamAnalyticsRows.map((row) => (
                          <tr key={row.name}>
                            <td><span className="dv2-user-dot" />{row.name}</td>
                            <td>{row.pickedOrders}</td>
                            <td>{row.packedOrders}</td>
                            <td>{row.pickedOrders + row.packedOrders}</td>
                            <td>{formatDuration(row.averageProcessingTimeMs)}</td>
                            <td>{row.corrections}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  ) : <div className="dv2-empty">No user activity for today</div>}
                </section>
                </div>
              </main>

              {/* ───── Right rail ───── */}
              <aside className="dv2-rail">
                {/* Attention / Stock issues */}
                <section className={`dv2-card dv2-attention${stockIssues.length > 0 ? ' alert' : ''}`}>
                  <div className="dv2-card-head">
                    <div>
                      <h2>Needs attention</h2>
                      <span>Stock issues today · tap to view orders</span>
                    </div>
                    {stockIssues.length > 0 && (
                      <span className="dv2-badge bad">{stockIssueTotal}</span>
                    )}
                  </div>

                  {stockIssues.length > 0 ? (
                    <div className="dv2-issues">
                      {stockIssues.map((issue, index) => (
                        <div
                          key={`${issue._id}-${index}`}
                          className="dv2-issue"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleStockIssueClick(issue._id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleStockIssueClick(issue._id);
                            }
                          }}
                        >
                          <span className="dv2-issue-dot" />
                          <span className="dv2-issue-name" title={issue._id}>
                            {issue._id}
                          </span>
                          <span className="dv2-badge bad">{issue.count}</span>
                          <RightOutlined className="dv2-issue-go" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="dv2-allclear">
                      <CheckCircleOutlined />
                      <div>
                        <b>All clear</b>
                        <span>No stock issues reported today</span>
                      </div>
                    </div>
                  )}
                </section>

                {/* Warehouse */}
                <section className="dv2-card">
                  <div className="dv2-card-head">
                    <div>
                      <h2>Warehouse status</h2>
                      <span>Dispatched from warehouse</span>
                    </div>
                  </div>
                  <div className="dv2-wh">
                    <div className="dv2-wh-total">
                      <b>{whTotal}</b>
                      <span>orders</span>
                    </div>
                    <div className="dv2-stack" aria-hidden="true">
                      {whTotal > 0 ? (
                        <>
                          <div className="yes" style={{ width: `${pct(whYes, whTotal)}%` }} />
                          <div className="no" style={{ width: `${pct(whNo, whTotal)}%` }} />
                        </>
                      ) : (
                        <div className="none" />
                      )}
                    </div>
                    <ul className="dv2-wh-legend">
                      <li>
                        <i className="yes" />
                        <span>Out: Yes</span>
                        <b>{whYes}</b>
                        <em>{pct(whYes, whTotal)}%</em>
                      </li>
                      <li>
                        <i className="no" />
                        <span>Out: No</span>
                        <b>{whNo}</b>
                        <em>{pct(whNo, whTotal)}%</em>
                      </li>
                    </ul>
                  </div>
                </section>

                {/* ───── Today's shipments – under Warehouse ───── */}
                <section className="dv2-card">
                  <div className="dv2-card-head">
                    <div>
                        <h2>Today's shipments</h2>
                      <span>
                        {courierTotal} {courierTotal === 1 ? 'shipment' : 'shipments'} across{' '}
                        {courierRows.length} {courierRows.length === 1 ? 'courier' : 'couriers'}
                      </span>
                    </div>
                  </div>
                  {courierRows.length > 0 ? (
                    <div className="dv2-couriers" style={{ gridTemplateColumns: '1fr' }}>
                      {courierRows.map((row) => {
                        const courier = getCourierAsset(row._id);
                        const share = pct(row.count, courierTotal);
                        return (
                          <div className="dv2-courier" key={row._id}>
                            <div className="dv2-courier-logo">
                              {courier.image ? (
                                <img src={courier.image} alt={courier.label} />
                              ) : (
                                <TruckOutlined />
                              )}
                            </div>
                            <div className="dv2-courier-body">
                              <div className="dv2-courier-name" title={row._id}>
                                {row._id}
                              </div>
                              <div className="dv2-meter">
                                <div style={{ width: `${share}%` }} />
                              </div>
                            </div>
                            <div className="dv2-courier-count">
                              <b>{row.count}</b>
                              <span>{share}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="dv2-empty">No shipments for today</div>
                  )}
                </section>
                <section className="dv2-card dv2-active-users-panel" aria-label="Active users">
                  <div className="dv2-card-head">
                    <div>
                      <h2>Active users</h2>
                      <span>Users who logged in during the last 30 minutes</span>
                    </div>
                    <span className="dv2-badge">{activeLoginUsers.length} online</span>
                  </div>
                  {activeLoginUsers.length > 0 ? (
                    <div className="dv2-active-users-list">
                      {activeLoginUsers.map((user) => (
                        <div className="dv2-active-user" key={String(user.userId)}>
                          <span className="dv2-active-user-dot" />
                          <div>
                            <strong>{user.name}</strong>
                            <span>{user.email}</span>
                          </div>
                          <time>{moment(user.lastLoginAt).format('hh:mm A')}</time>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="dv2-empty dv2-active-users-empty">No recent login activity</div>
                  )}
                </section>
                <section className="dv2-card dv2-star-performers-card">
                  <div className="dv2-card-head">
                    <div>
                      <h2>Star performers</h2>
                      <span>Today’s top operation performers</span>
                    </div>
                    <span className="dv2-star-label">★ Top performers</span>
                  </div>
                  <div className="dv2-star-performers-grid">
                    <div className="dv2-star-performer">
                      <div className="dv2-star-icon">★</div>
                      <div className="dv2-star-performer-body">
                        <span className="dv2-star-operation">Picking</span>
                        <strong>{pickingStarPerformer?.name || 'No picking data'}</strong>
                        <small>{pickingStarPerformer ? `${pickingStarPerformer.count} picked operations` : 'No completed operations today'}</small>
                      </div>
                      <div className="dv2-gold-stars" aria-label="Three star performer">★★★</div>
                    </div>
                    <div className="dv2-star-performer">
                      <div className="dv2-star-icon">★</div>
                      <div className="dv2-star-performer-body">
                        <span className="dv2-star-operation">Packing</span>
                        <strong>{packingStarPerformer?.name || 'No packing data'}</strong>
                        <small>{packingStarPerformer ? `${packingStarPerformer.count} packed orders` : 'No completed operations today'}</small>
                      </div>
                      <div className="dv2-gold-stars" aria-label="Three star performer">★★★</div>
                    </div>
                  </div>
                </section>
              </aside>
            </div>
          </>
        )}

        <DashboardEmailModal
          visible={emailModalVisible}
          onClose={() => setEmailModalVisible(false)}
          dashboardData={{
            scheduledForPackingToday,
            todayPickedCount,
            todayStats,
            todayNotCompletedCount,
            overallStats,
            courierShipments,
            packedPersons,
            pickedPersons,
          }}
          darkTheme={darkTheme}
          defaultRecipients={defaultEmailRecipients}
        />
      </div>
    </div>
  );
}

export default DashboardPage;
